import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { gatherDataWithAI } from '@/utils/aiAgentService';
import { Question, SurveyResponse, Answer } from '@/types/survey';
import { Progress } from '@/components/ui/progress';
import { Bot, AlertCircle, HelpCircle, CheckCircle2, Lightbulb, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { useLanguage } from '@/contexts/LanguageContext';

interface AIDataGatheringProps {
  projectName: string;
  industry: string;
  questions: Question[];
  onDataGathered: (responses: SurveyResponse) => void;
}

const AIDataGathering: React.FC<AIDataGatheringProps> = ({ 
  projectName, 
  industry, 
  questions, 
  onDataGathered 
}) => {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [projectDescription, setProjectDescription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [guidanceGenerated, setGuidanceGenerated] = useState(false);
  const [questionGuidance, setQuestionGuidance] = useState<{[questionId: number]: string}>({});
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);
  const [previousDescription, setPreviousDescription] = useState<string>('');
  
  const handleGenerateGuidance = async () => {
    if (!projectDescription || projectDescription.length < 20) {
      toast({
        title: t('survey.text.required').replace('{count}', '20'),
        description: t('survey.text.minWords').replace('{count}', '20'),
        variant: "destructive"
      });
      return;
    }
    
    // Skip if using the same description to prevent repeated guidance
    if (projectDescription === previousDescription && guidanceGenerated) {
      toast({
        title: t('survey.ai.guidance.unchanged') || "Description unchanged",
        description: t('survey.ai.guidance.modify') || "Please modify your project description to generate new guidance",
      });
      return;
    }
    
    setIsProcessing(true);
    setProgress(10);
    setQuestionGuidance({});
    setGuidanceGenerated(false);
    
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.floor(Math.random() * 10) + 5;
      });
    }, 300);
    
    try {
      const result = await gatherDataWithAI(
        projectName,
        industry,
        projectDescription,
        questions
      );
      
      clearInterval(progressInterval);
      
      if (result.success && result.data) {
        setProgress(100);
        setGuidanceGenerated(true);
        setPreviousDescription(projectDescription);
        
        const guidance: {[questionId: number]: string} = {};
        
        // Transform the explanations into guidance
        questions.forEach(question => {
          const questionId = question.id;
          
          if (result.data.questionExplanations && result.data.questionExplanations[questionId]) {
            guidance[questionId] = result.data.questionExplanations[questionId];
          }
        });
        
        setQuestionGuidance(guidance);
        
        if (questions.length > 0) {
          setExpandedAccordion(`q-${questions[0].id}`);
        }
        
        setTimeout(() => {
          toast({
            title: t('survey.ai.applied'),
            description: t('survey.ai.description'),
          });
        }, 500);
      } else {
        throw new Error(result.error || "Unknown error");
      }
    } catch (error) {
      clearInterval(progressInterval);
      toast({
        title: t('survey.ai.guidance.failed') || "Guidance generation failed",
        description: error instanceof Error ? error.message : t('survey.ai.guidance.error') || "An error occurred",
        variant: "destructive"
      });
    } finally {
      setTimeout(() => setIsProcessing(false), 500);
    }
  };
  
  const generateGuidanceFromExplanation = (
    explanation: string, 
    question: Question,
    industry: string
  ): string => {
    // Generate thoughtful guidance based on the question category and industry
    const baseGuidance = `${t('survey.ai.guidance.consider') || "Consider how"} ${projectName} ${t('survey.ai.guidance.in') || "in the"} ${industry} ${t('survey.ai.guidance.industry') || "industry might approach this."} `;
    
    let methodologyGuidance = '';
    switch(question.category) {
      case 'ea':
      case 'ee':
        methodologyGuidance = t('methodology.ea.guidance') || 
          `Think about emotional connections users form with brands. Consider factors like brand recognition, user community, and personal attachment. How does your project create meaningful relationships?`;
        break;
      case 'elv':
      case 'lq':
        methodologyGuidance = t('methodology.elv.guidance') || 
          `Consider the lasting impact and ethical foundations of your project. What values does it represent? How does it contribute positively to society or the industry?`;
        break;
      case 'l':
      case 'fc':
        methodologyGuidance = t('methodology.l.guidance') || 
          `Reflect on sustainability and resilience factors. How does your project adapt to changes? What ensures its relevance over time?`;
        break;
      case 'tb':
        methodologyGuidance = t('methodology.tb.guidance') || 
          `Focus on trust-building elements. What makes stakeholders believe in your project? How do you establish and maintain credibility?`;
        break;
      default:
        methodologyGuidance = t('methodology.default.guidance') || 
          `Consider the specific characteristics of your project that relate to this question.`;
    }
    
    // Add industry-specific prompts
    let industryGuidance = '';
    if (industry === t('industry.technology') || industry === 'Technology') {
      industryGuidance = t('industry.technology_guidance') || 
        `In technology, consider innovation cycles, user experience design, and ethical tech practices.`;
    } else if (industry === t('industry.healthcare') || industry === 'Healthcare') {
      industryGuidance = t('industry.healthcare_guidance') || 
        `In healthcare, patient outcomes, care accessibility, and trust in providers are key considerations.`;
    } else if (industry === t('industry.finance') || industry === 'Finance') {
      industryGuidance = t('industry.finance_guidance') || 
        `For financial services, think about transparency, security, and how you build client confidence.`;
    } else if (industry === t('industry.education') || industry === 'Education') {
      industryGuidance = t('industry.education_guidance') || 
        `In education, learning outcomes, accessibility, and community building are important factors.`;
    } else if (industry === t('industry.web_30fintech') || industry === 'Web 3.0 & Fintech') {
      industryGuidance = 
        `In Web 3.0 & Fintech, consider decentralization principles, security, user experience, and trust-building mechanisms.`;
    }
    
    // Add question-specific prompts to help users think more deeply
    let questionPrompts = '';
    if (question.type === 'text') {
      questionPrompts = t('survey.text.prompts') || 
        `\n\nConsider these prompts to develop your answer:\n• What specific examples demonstrate this in your project?\n• How does this compare to industry standards?\n• What measurable outcomes support your assessment?`;
    }
    
    return `${baseGuidance}${methodologyGuidance} ${industryGuidance}${questionPrompts}`;
  };
  
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'ee':
      case 'ea': return t('metrics.emotional');
      case 'lq':
      case 'elv': return t('metrics.legacy');
      case 'fc':
      case 'l': return t('metrics.friction');
      case 'tb': return t('metrics.trust');
      case 'bc': return t('metrics.behavioral') || 'Behavioral Conversion';
      case 'ppr': return 'PPR Alert';
      default: return category;
    }
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bot className="mr-2 h-5 w-5" /> {t('survey.ai.guide')}
        </CardTitle>
        <CardDescription>
          {t('survey.ai.guide.description') || "Get AI-powered guidance to help you complete the assessment"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-500" />
            <AlertDescription>
              {t('survey.ai.instruction') || 'Instruction'}
            </AlertDescription>
          </Alert>
          
          {/* Example section to help users understand the feature better */}
          {!guidanceGenerated && (
            <Alert className="bg-green-50 border-green-200">
              <Info className="h-4 w-4 text-green-600" />
              <AlertTitle>How This Works</AlertTitle>
              <AlertDescription className="text-sm mt-1">
                <p>Provide a detailed description of your project (minimum 20 words). Our AI will then:</p>
                <ol className="list-decimal ml-5 mt-2 space-y-1">
                  <li>Analyze your project's industry context</li>
                  <li>Generate tailored guidance for each assessment question</li>
                  <li>Provide methodology explanation based on BEAM's framework</li>
                </ol>
                <p className="mt-2 italic">Example: "Our SaaS platform helps small businesses streamline their customer support with AI-powered chatbots and a unified dashboard for managing customer interactions across multiple channels."</p>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <label htmlFor="projectDescription" className="text-sm font-medium">
              {t('survey.project.description') || 'Project Description'}
            </label>
            <Textarea
              id="projectDescription"
              placeholder={t('survey.describe.placeholder')}
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              rows={5}
              className="resize-none"
              disabled={isProcessing}
            />
            {previousDescription && guidanceGenerated && previousDescription === projectDescription && (
              <p className="text-xs text-amber-600 mt-1">
                {t('survey.ai.guidance.change_desc') || "Change your description to generate new guidance"}
              </p>
            )}
          </div>
          
          {isProcessing && (
            <div className="space-y-2 py-2">
              <div className="flex justify-between text-sm">
                <span>{t('survey.generating.guidance')}</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}
          
          {guidanceGenerated && !isProcessing && (
            <div className="p-3 bg-gray-50 rounded-md text-sm space-y-4">
              <div>
                <p className="font-medium text-gray-900">{t('survey.ai.methodology') || "Methodology Guide"}:</p>
                <p className="text-gray-700 mt-1">{t('survey.ai.methodology.description') || "This guide provides insights based on BEAM's assessment framework"}</p>
              </div>
              
              {Object.keys(questionGuidance).length > 0 && (
                <div>
                  <p className="font-medium text-gray-900 mb-2">{t('survey.question.guidance')}:</p>
                  <Accordion 
                    type="single" 
                    collapsible 
                    className="w-full"
                    value={expandedAccordion || undefined}
                    onValueChange={(value) => setExpandedAccordion(value)}
                  >
                    {questions.map((question) => (
                      <AccordionItem value={`q-${question.id}`} key={question.id}>
                        <AccordionTrigger className="text-sm">
                          <div className="flex items-center justify-between w-full pr-4">
                            <div className="flex items-center">
                              <span className="text-xs font-medium bg-gray-200 rounded-full h-5 w-5 flex items-center justify-center mr-2">
                                {question.id}
                              </span>
                              <span className="truncate" style={{maxWidth: "280px"}}>
                                {question.text}
                              </span>
                            </div>
                            <span className="ml-2 text-xs font-semibold text-primary">
                              <Lightbulb className="h-3 w-3" />
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pl-7 pr-2 text-sm">
                            <div className="flex items-center text-xs text-gray-500 mb-1">
                              <span className="uppercase font-semibold">{getCategoryLabel(question.category)}</span>
                            </div>
                            <div className="mb-2 text-gray-700 space-y-2">
                              <div className="space-y-1">
                                <h4 className="font-medium">{t('survey.methodology')}:</h4>
                                <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-200">
                                  {question.category === 'ee' || question.category === 'ea' ? 
                                    t('methodology.ea.description') : 
                                    question.category === 'lq' || question.category === 'elv' ? 
                                    t('methodology.elv.description') : 
                                    question.category === 'fc' || question.category === 'l' ? 
                                    t('methodology.l.description') : 
                                    question.category === 'tb' ?
                                    t('methodology.ea.metric4') :
                                    t('survey.methodology_bc')
                                  }
                                </p>
                              </div>
                              <div className="space-y-1">
                                <h4 className="font-medium">{t('survey.ai.guidance')}:</h4>
                                <p className="text-xs text-gray-600 pl-2 border-l-2 border-primary">
                                  {questionGuidance[question.id] || t('survey.no.specific.guidance')}
                                </p>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-3">
        <Button 
          onClick={handleGenerateGuidance} 
          disabled={isProcessing || projectDescription.length < 20}
          className="w-full"
        >
          {isProcessing ? 
            t('survey.processing') : 
            guidanceGenerated && previousDescription === projectDescription ?
            t('survey.regenerate.guidance') || "Regenerate Guidance" :
            t('survey.generate.guidance') || "Generate Guidance"
          }
        </Button>
        
        {guidanceGenerated && !isProcessing && (
          <p className="text-xs text-gray-500 text-center mt-1">
            {t('survey.ai.guidance.help') || "Review the guidance for each question to help formulate your responses"}
          </p>
        )}
      </CardFooter>
    </Card>
  );
};

export default AIDataGathering;
